/**
 * What kind of home a project record describes.
 *
 * The backend has no field for this. `property_type` is free text and
 * currently reads "Condo" on executive condominium projects too, which is
 * why EC pages were showing private-condo eligibility, stamp duty and
 * financing answers. Until the backend carries a real flag, we derive it.
 *
 * Derivation is deliberately narrow:
 *  - "executive condo"/"executive condominium" anywhere in type/name/title
 *  - a standalone "EC" token, so "EC" matches but "ECO Sanctuary" does not
 *
 * When the backend gains a proper field, keep this signature and read the
 * field first — every caller stays unchanged.
 */

export interface PropertyKindSource {
  name?: string | null;
  title?: string | null;
  property_type?: string | null;
}

const EC_WORDS = /executive\s+condo(?:minium)?/i;
/** Standalone EC token: "Solano Grand EC", "Woodlands Dr 17 (EC)", "EC site". */
const EC_TOKEN = /(^|[^A-Za-z])EC([^A-Za-z]|$)/;

export function isExecutiveCondo(p: PropertyKindSource | null | undefined): boolean {
  if (!p) return false;
  const haystack = [p.property_type, p.title, p.name]
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .join(" · ");
  if (!haystack) return false;
  return EC_WORDS.test(haystack) || EC_TOKEN.test(haystack);
}

/**
 * Label for the "Type" row in Key facts. The stored value stays untouched;
 * this only decides what the page says, so an EC record that still reads
 * "Condo" in the CMS no longer contradicts its own eligibility copy.
 */
export function propertyTypeLabel(p: PropertyKindSource | null | undefined): string {
  if (isExecutiveCondo(p)) return "Executive condominium (EC)";
  return p?.property_type?.trim() || "";
}

/**
 * Residential or not. The nearby-launches widget uses this to keep food
 * factories and industrial units out of a homebuyer's shortlist.
 *
 * Unknown or empty types count as residential: nearly every record is a
 * home, and hiding a real project is worse than showing one extra.
 */
const NON_RESIDENTIAL_TYPE =
  /\b(food\s*factory|factory|industrial|warehouse|workshop|office|shop\s*house|retail|commercial|b1|b2)\b/i;

/**
 * Some industrial records are typed "Condo" in the CMS (Food Vision @ Mandai
 * and CT FoodNEX both are), so the type alone cannot filter them out. These
 * name tokens are deliberately narrow: each one names an industrial product,
 * not a word a condo would plausibly use.
 *
 * This is a guard, not the fix. Correcting property_type on those records
 * removes the need for it.
 */
const NON_RESIDENTIAL_NAME =
  /\b(foodnex|food\s*(vision|factory|hub)|industrial|logistics|warehouse|biz\s*hub|ramp[-\s]?up)\b/i;

export function isResidential(p: PropertyKindSource | null | undefined): boolean {
  const type = p?.property_type?.trim();
  if (type && NON_RESIDENTIAL_TYPE.test(type)) return false;
  const named = [p?.title, p?.name].filter((v): v is string => typeof v === "string").join(" · ");
  if (named && NON_RESIDENTIAL_NAME.test(named)) return false;
  return true;
}
